import React from "react";
import { useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { FaBox, FaTruck, FaCheckCircle } from "react-icons/fa";
import { useQuery } from "@tanstack/react-query";

const OrderDetail = () => {
  const { id } = useParams();
  const API_URL = process.env.REACT_APP_API_URL;

  const {
    data: order,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ["order", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await fetch(`${API_URL}/order/order/${id}`, {
        headers: { Authorization: `Bearer ${Cookies.get("gameport")}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed to load order");
      return data;
    },
  });

  if (isPending) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-8">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <p className="text-gray-600">Loading order...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-8">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <p className="text-red text-sm">{error?.message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-8">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <p className="text-gray-600">Order not found.</p>
          </div>
        </div>
      </div>
    );
  }

  const statusSteps = ["processing", "shipped", "delivered"];
  const currentStep = statusSteps.indexOf(order.orderStatus);

  const statusLabel = String(order?.orderStatus || "processing").toUpperCase();
  const isCancelled = order?.orderStatus === "cancelled";

  const placedOn = order?.createdAt
    ? new Date(order.createdAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-8 sm:py-10 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
            Order Details
          </h1>

          <span
            className={`inline-flex items-center justify-center px-4 py-2 rounded-full text-xs sm:text-sm font-bold tracking-wide
              ${isCancelled ? "bg-red text-white" : "bg-gray-900 text-white"}`}
          >
            {statusLabel}
          </span>
        </div>

        {/* Order Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h2 className="font-bold text-lg text-gray-900 mb-3">Order Info</h2>

            <div className="space-y-2 text-sm text-gray-700">
              <p className="uppercase text-gray-900 font-semibold">
                <span className="text-gray-600 font-bold">Order ID:</span> #{order?.orderCode}
              </p>
              <p className="uppercase text-gray-900 font-semibold">
                <span className="text-gray-600 font-bold">Placed On:</span> {placedOn}
              </p>
              <p className="uppercase text-gray-900 font-semibold">
                <span className="text-gray-600 font-bold">Payment Status:</span>{" "}
                {String(order?.status || "").toUpperCase() || "—"}
              </p>
              <p className="uppercase text-gray-900 font-semibold">
                <span className="text-gray-600 font-bold">Order Status:</span>{" "}
                {String(order?.orderStatus || "").toUpperCase() || "—"}
              </p>
              <p className="uppercase text-gray-900 font-semibold">
                <span className="text-gray-600 font-bold">Payment Method:</span>{" "}
                {order?.paymentMethod || "—"}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h2 className="font-bold text-lg text-gray-900 mb-3">
              Shipping Address
            </h2>

            <div className="space-y-1 text-sm text-gray-700">
              <p className="break-words">{order?.shippingAddress?.address || "—"}</p>
              <p className="break-words">
                {order?.shippingAddress?.city || "—"}
                {order?.shippingAddress?.country ? `, ${order.shippingAddress.country}` : ""}
              </p>
              {order?.shippingAddress?.telephone && (
                <p className="break-words">{order.shippingAddress.telephone}</p>
              )}
              {order?.shippingAddress?.email && (
                <p className="break-words">{order.shippingAddress.email}</p>
              )}
            </div>
          </div>
        </div>

        {/* Status Tracker (vertical on mobile, horizontal on md+) */}
        {!isCancelled && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
            <h2 className="font-bold text-lg text-gray-900 mb-4">
              Order Progress
            </h2>

            {/* Mobile: vertical */}
            <div className="md:hidden space-y-4">
              {statusSteps.map((step, index) => {
                const completed = index <= currentStep;
                return (
                  <div key={step} className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${
                        completed ? "bg-gray-900" : "bg-gray-300"
                      }`}
                    >
                      {step === "processing" && <FaBox />}
                      {step === "shipped" && <FaTruck />}
                      {step === "delivered" && <FaCheckCircle />}
                    </div>

                    <div className="flex-1">
                      <p className="font-semibold capitalize text-gray-900">
                        {step}
                      </p>
                      <p className="text-xs text-gray-500">
                        {completed ? "Completed" : "Pending"}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop: horizontal */}
            <div className="hidden md:flex items-center justify-between">
              {statusSteps.map((step, index) => {
                const completed = index <= currentStep;
                const isLast = index === statusSteps.length - 1;

                return (
                  <div key={step} className="flex-1 flex items-center">
                    <div className="flex flex-col items-center w-full">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${
                          completed ? "bg-gray-900" : "bg-gray-300"
                        }`}
                      >
                        {step === "processing" && <FaBox />}
                        {step === "shipped" && <FaTruck />}
                        {step === "delivered" && <FaCheckCircle />}
                      </div>
                      <p className="text-sm font-semibold mt-2 text-gray-700 capitalize">
                        {step}
                      </p>
                    </div>

                    {!isLast && (
                      <div
                        className={`h-1 w-full ${
                          index < currentStep ? "bg-gray-900" : "bg-gray-300"
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Items */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
            Items in Order
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {order?.items?.map((item) => (
              <div
                key={item._id}
                className="flex items-center gap-4 rounded-2xl border border-gray-100 p-4"
              >
                <img
                  src={item?.product?.images?.[0] || "/placeholder.png"}
                  alt={item?.name || "Item"}
                  className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-xl border border-gray-100"
                />

                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    {item?.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    ₦{Number(item?.finalPrice || 0).toLocaleString()} × {item?.quantity}
                  </p>
                </div>

                <p className="font-bold text-gray-900 text-sm sm:text-base">
                  ₦{Number(item?.finalPrice * item?.quantity || 0).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Total */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6 flex justify-end">
          <p className="font-bold text-lg text-gray-900">
            Total: ₦{Number(order?.total || 0).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
